/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import {Injectable} from '@angular/core';
import {Router} from "@angular/router";

import {AuthenticationService} from './authentication.service';
import {BaseService} from "./base-service";

import {Antenna} from '../models/antenna';
import {AntennaInstance} from '../models/antenna-instance';
import {Point} from "../models/point";
import {PropertyPatch} from "../models/property-patch";
import { PagedResults } from 'app/models/paged-results';
import { RestService } from './rest-service';
import { Url_Base } from 'app/rest-constants';

@Injectable()
export class AntennaService extends BaseService {

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private mRestService: RestService
  ) {
    super(router, authenticationService);
  }

  private getUrl(): string {
    return `${Url_Base}/rfidservers/readers/antennas/`;
  }


  getNumberAntennas(): Promise<Number> {
    const callUrl = this.getUrl() + 'count';
    return this.mRestService.get<Number>(callUrl);
  }

  getNumberAntennaInstances(): Promise<Number> {
    const callUrl = this.getUrl() + 'instances/count';
    return this.mRestService.get<Number>(callUrl);
  }

  getAllAntennasForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<Antenna>> {
    const callUrl = this.getUrl() + 'paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<PagedResults<Antenna>>(callUrl);
  }

  getAllAntennaInstancesForPaging(start: number, size: number, order: String, filterString: String): Promise<PagedResults<AntennaInstance>> {
    const callUrl = this.getUrl() + 'instances/paging?start=' + start + "&size=" + size + "&order=ASC&filterColumn=name&filterString=" + filterString;
    return this.mRestService.get<PagedResults<AntennaInstance>>(callUrl);
  }

  getAllAntennas(): Promise<Antenna[]> {
    const callUrl = this.getUrl();
    return this.mRestService.get<Antenna[]>(callUrl);
  }

  getAntennaById(id: number): Promise<Antenna> {
    const callUrl = this.getUrl() + `${id}`;
    return this.mRestService.get<Antenna>(callUrl);
  }

  createAntenna(antenna: Antenna): Promise<Antenna> {
    const callUrl = this.getUrl();
    return this.mRestService.post<Antenna>(callUrl, antenna);
  }

  updateAntenna(antenna: Antenna): Promise<Antenna> {
    const callUrl = this.getUrl();
    return this.mRestService.put<Antenna>(callUrl, antenna);
  }

  deleteAntennas(ids: number[]): Promise<any> {
    const callUrl = this.getUrl();
    return this.mRestService.delete(callUrl, ids)
  }


 /*-----------------------INSTANCE FUNCTIONS FOR ANTENNA -------------------------*/



  getAllAntennaInstances(): Promise<AntennaInstance[]> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.get<AntennaInstance[]>(callUrl);
  }

  getAntennaInstancesFiltered(start: number, size: number, order: string, filterString: string): Promise<AntennaInstance[]> {
    const url = this.getUrl() + `instances/filtered?start=` + start + "&size=" + size + "&order=" + order + "&filterColumn=name&filterString=" + filterString;
    const response =  this.mRestService.get<AntennaInstance[]>(url);
    return response;
  }

  getAntennaInstanceById(id: number): Promise<AntennaInstance> {
    const callUrl = this.getUrl() + `instances/${id}`;
    return this.mRestService.get<AntennaInstance>(callUrl);
  }

  createAntennaInstance(antennaInstance: AntennaInstance): Promise<AntennaInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.post<AntennaInstance>(callUrl, antennaInstance);
  }

  updateAntennaInstance(antennaInstance: AntennaInstance): Promise<AntennaInstance> {
    const callUrl = this.getUrl() + 'instances';
    return this.mRestService.put<AntennaInstance>(callUrl, antennaInstance);
  }

  setAntennaInstancePosition(antennaInstanceId: number, locationId: number, position: Point): Promise<AntennaInstance> {
    const callUrl = this.getUrl() + `instances/${antennaInstanceId}/set-position`;
    const dto = { location_id: locationId, position: position };
    return this.mRestService.put<AntennaInstance>(callUrl, dto);
  }

  deleteAntennaInstances(ids: number[]): Promise<any> {
    const callUrl = this.getUrl() + `instances`;
    return this.mRestService.delete(callUrl, ids);
    }

  executeFunction(antennaInstanceId: number, functionId: number, propertyPatches: PropertyPatch[], responseType?: string): Promise<any> {
    const callUrl = this.getUrl() + `instances/${antennaInstanceId}/execute-function?function_id=${functionId}`;
    return this.mRestService.post(callUrl, propertyPatches, responseType);
  }

}
